import { NotificationResponseDto } from '@app/api';
import { COLORS, TYPOGRAPHY } from '@app/constants';
import {
  useClearNotificationsMutation,
  useGetNotificationsQuery,
  useMarkAllNotificationsViewedMutation,
} from '@app/store/api/notifications';
import { BreakpointsEnum } from '@app/types';
import FlatBtn from '@app/ui/components/FlatBtn';
import IconBtn from '@app/ui/components/IconBtn';
import { ArrowDown, Delete } from '@app/ui/iconComponents';
import { getDecimalWithNum } from '@app/utils/getDecimalWithNum';
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Components, Virtuoso } from 'react-virtuoso';
import styled from 'styled-components';
import NotificationItem from './notificationItem';

interface Props {
  className?: string;
  onClose: () => void;
}

const NotificationsDropdown: FC<Props> = ({ className, onClose }) => {
  const [page, setPage] = useState(1);
  const { data } = useGetNotificationsQuery({ limit: 10, page }, { refetchOnMountOrArgChange: true });
  const [loadedData, setLoadedData] = useState<NotificationResponseDto[]>([]);

  useLayoutEffect(() => {
    if (data && data.total > loadedData.length) {
      setLoadedData([...loadedData, ...data.data]);
    }
  }, [data]);

  const [markAllNotificationViewed] = useMarkAllNotificationsViewedMutation();

  const [clearNotifications] = useClearNotificationsMutation();

  useEffect(() => {
    if (data?.notViewedCount) {
      markAllNotificationViewed();
    }
  }, [data, markAllNotificationViewed]);

  const onLoadMore = useCallback(() => {
    if (data && data.totalPage > page) {
      setPage(page + 1);
    }
  }, [data, page]);

  const putItemContent = useCallback((_index: number, notification: NotificationResponseDto) => {
    return <NotificationItem notification={notification} />;
  }, []);

  const handleClear = () => {
    setLoadedData((prevValue) => {
      return prevValue.filter((item) => item.event === 'greetings-therapist' || item.event === 'greetings-patient');
    });
    clearNotifications();
  };

  const isDisabledClearBtn = useMemo(
    () => !loadedData.find((item) => item.event !== 'greetings-therapist' && item.event !== 'greetings-patient'),
    [loadedData],
  );

  return (
    <Root className={className}>
      <Wrapper>
        <Header>
          <StyledIconBtn
            styleType="flat"
            onClick={onClose}
          >
            <ArrowDown />
          </StyledIconBtn>
          <Title>
            Уведомления{' '}
            {data?.notViewedCount
              ? data.notViewedCount > 0 && (
                  <span>({getDecimalWithNum(data.notViewedCount, ['новое', 'новых', 'новых'])})</span>
                )
              : null}
          </Title>
          <StyledFlatBtn
            styleType="primary"
            type="button"
            isDisabled={isDisabledClearBtn}
            onClick={handleClear}
          >
            Очистить
          </StyledFlatBtn>

          <DeleteBtn
            styleType="flat"
            type="button"
            isDisabled={isDisabledClearBtn}
            onClick={handleClear}
          >
            <Delete color={COLORS.primary300} />
          </DeleteBtn>
        </Header>
        {data?.data.length ? (
          <Virtuoso
            style={{
              flex: '1',
              overscrollBehavior: 'contain',
            }}
            totalCount={loadedData.length}
            components={{
              List: List as Components['List'],
              Item: ListItem as Components['Item'],
            }}
            endReached={onLoadMore}
            data={loadedData}
            itemContent={putItemContent}
          />
        ) : (
          <EmptyStateWrapper>
            <EmptyStateText>Нет новых уведомлений</EmptyStateText>
          </EmptyStateWrapper>
        )}
      </Wrapper>
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 24px rgba(75, 100, 137, 0.1);
  border-radius: 16px;
  background-color: ${COLORS.white};

  @media (min-width: ${BreakpointsEnum.sm}px) {
    width: max-content;
    min-width: 464px;
    max-width: 464px;
    min-height: 287px;
  }
`;

const Wrapper = styled.div`
  padding-left: 20px;
  padding-right: 20px;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  flex: 1 1 auto;
`;

const List = styled.ul``;

const ListItem = styled.li``;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  padding-bottom: 16px;
`;

const StyledIconBtn = styled(IconBtn)`
  display: none;
  @media (max-width: ${BreakpointsEnum.sm}px) {
    display: block;
    transform: rotate(90deg);
  }
`;

const Title = styled.p`
  ${TYPOGRAPHY.headline2Semibold16};
  color: ${COLORS.grayscale700};

  span {
    color: ${COLORS.grayscale300};
  }
`;

const DeleteBtn = styled(IconBtn)`
  display: none;
  @media (max-width: ${BreakpointsEnum.sm}px) {
    display: block;
  }
`;

const StyledFlatBtn = styled(FlatBtn)`
  display: block;
  @media (max-width: ${BreakpointsEnum.sm}px) {
    display: none;
  }
`;

const EmptyStateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 74px;
  padding-bottom: 54px;
`;

const EmptyStateText = styled.p`
  ${TYPOGRAPHY.headline2Medium16};
  color: ${COLORS.grayscale300};
`;

export default NotificationsDropdown;
